import React from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router'
import { useBeforeUnload } from 'react-use'

import { HStack } from '~/components/elements/HStack'
import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { exists } from '~/utils'

import { isActiveGenerationPhase } from '../atoms'
import { useBasicOutputGenerationFlow, useBasicOutputWizard } from '../hooks'

import { BrandSelectionStep } from './BrandSelectionStep'
import { ConfirmationStep } from './ConfirmationStep'
import { FilterPatternStep } from './FilterPatternStep'
import { ProgressStep } from './ProgressStep'
import { SurveyContextStep } from './SurveyContextStep'

type BasicOutputWizardProps = {
  surveyId: string
}

export const BasicOutputWizard = ({ surveyId }: BasicOutputWizardProps) => {
  const { t } = useTranslation()
  const [currentStep, setCurrentStep] = React.useState(1)
  const surveyIdRef = React.useRef(surveyId)
  const { reset: resetWizard } = useBasicOutputWizard()
  const flow = useBasicOutputGenerationFlow({ surveyId })
  const { setAiOn } = flow
  const logEvent = useLogEvent()

  // Wizard state lives in module-level jotai atoms, which persist across route
  // remounts. Reset them when the wizard opens AND when the survey changes
  // (React Router reuses this instance across :surveyId navigations) so a
  // re-entry starts clean. The per-execution AI toggle is component-local
  // useState (not a jotai atom), so resetWizard() does not clear it; reset it
  // to its default (ON) here too, otherwise an AI-off choice from one survey
  // would silently carry into the next.
  React.useEffect(() => {
    surveyIdRef.current = surveyId
    resetWizard()
    setAiOn(true)
    // currentStep is component-local useState (not a jotai atom), so resetWizard()
    // does not touch it. Reset it to the first step too, otherwise a survey change
    // would leave the user on a later step showing the freshly-reset state.
    setCurrentStep(1)
  }, [resetWizard, setAiOn, surveyId])

  const goNext = React.useCallback(
    () => setCurrentStep((s) => Math.min(s + 1, 5)),
    [],
  )
  const goBack = React.useCallback(
    () => setCurrentStep((s) => Math.max(s - 1, 1)),
    [],
  )

  const handleSubmit = React.useCallback(async () => {
    // Capture the survey before the await: this component instance is reused
    // across :surveyId navigations. If the user switches survey while
    // startGeneration is in flight, the continuation must not advance the new
    // survey to step 5 (the submit also abandons via its own epoch guard).
    // Compare against surveyIdRef (updated by the reset effect) rather than the
    // closure's surveyId, which is frozen at this callback's creation.
    const submittedSurveyId = surveyId
    const generationId = await flow.startGeneration()
    if (submittedSurveyId !== surveyIdRef.current) {
      return
    }
    if (generationId) {
      logEvent('Generate basic output')
    }
    // Advance to the progress step regardless of submit outcome. On success the
    // phase is already 'tabulating' (progress shown). On an INITIAL-submit
    // failure the phase stays 'input' but flow.submitError is set, so the
    // recovery effect below keeps us on step 5 and ProgressStep surfaces the
    // error — previously a failed submit returned silently, stranding the user
    // on the confirmation step with no feedback. On a clean no-op (no
    // generationId, no error) the recovery effect returns to step 4.
    setCurrentStep(5)
  }, [flow, logEvent, surveyId])

  // Recover from a later-phase failure on the progress step. A phase after
  // submit (polling/rendering/commenting/documenting) resets phase to 'input'
  // on failure while currentStep stays 5, leaving ProgressStep blank with no
  // controls — return the user to the confirmation step to retry. EXCEPTION: an
  // initial-submit failure also leaves phase 'input' but sets submitError; keep
  // the user on step 5 so ProgressStep surfaces the error instead of silently
  // bouncing them back.
  React.useEffect(() => {
    if (
      currentStep === 5 &&
      flow.phase === 'input' &&
      !exists(flow.submitError)
    ) {
      setCurrentStep(4)
    }
  }, [currentStep, flow.phase, flow.submitError])

  const isGenerating =
    flow.isSubmitting ||
    (currentStep === 5 && isActiveGenerationPhase(flow.phase))
  const warningMessage = t('basic_output_do_not_close_warning')
  // useBeforeUnload only covers tab close / reload. In-app React Router
  // navigation (side menu, breadcrumb, SPA back) needs a router blocker too,
  // otherwise the warning text lies during active generation. Same pattern as
  // StrategyMemoEditPrompt.
  useBeforeUnload(isGenerating, warningMessage)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isGenerating && currentLocation.pathname !== nextLocation.pathname,
  )
  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm(warningMessage)) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker, warningMessage])

  // Shown on both Step 1 and Step 4: the underlying aiEnabledQuery is a single
  // instance shared across the whole wizard, so a failure that occurs after
  // the user has left Step 1 would otherwise be invisible — Step 4 only
  // disabled its buttons via isAiStateResolved with no explanation or retry.
  const aiStateErrorBanner = exists(flow.aiEnabledError) && (
    <VStack spacing={1}>
      <Typography color="error" variant="small">
        {t('basic_output_ai_state_error')}
      </Typography>
      <HStack spacing={2}>
        <StyledButton buttonStyle="light" onClick={flow.retryAiEnabled}>
          {t('basic_output_retry')}
        </StyledButton>
      </HStack>
    </VStack>
  )

  const stepContent = (() => {
    switch (currentStep) {
      case 1:
        return (
          <VStack spacing={2}>
            {aiStateErrorBanner}
            <SurveyContextStep
              aiEnabled={flow.aiEnabled}
              effectiveAiOn={flow.effectiveAiOn}
              isAiStateResolved={flow.isAiStateResolved}
              onAiOnChange={flow.setAiOn}
              onNext={goNext}
            />
          </VStack>
        )
      case 2:
        return <BrandSelectionStep onBack={goBack} onNext={goNext} />
      case 3:
        return <FilterPatternStep onBack={goBack} onNext={goNext} />
      case 4:
        return (
          <VStack spacing={2}>
            {aiStateErrorBanner}
            <ConfirmationStep
              aiEnabled={flow.aiEnabled}
              effectiveAiOn={flow.effectiveAiOn}
              isAiStateResolved={flow.isAiStateResolved}
              isSubmitting={flow.isSubmitting || !flow.isAiStateResolved}
              onBack={goBack}
              onSubmit={handleSubmit}
            />
          </VStack>
        )
      case 5:
        return (
          <VStack spacing={2}>
            <ProgressStep
              aiEnabled={flow.aiEnabled}
              brandAccountId={flow.brandAccountId}
              commentProgress={flow.commentProgress}
              documentProgress={flow.documentProgress}
              error={flow.submitError}
              isLoading={flow.isSubmitting}
              phase={flow.phase}
              renderProgress={flow.renderProgress}
            />
            {/* On a failed generation the flow keeps the user on step 5 to show
                the error (submitError set), but ProgressStep has no navigation.
                Surface an explicit recovery control back to the confirmation
                step so re-submit clears the error and retries; this renders ONLY
                in the error state, never during a successful/in-progress run. */}
            {exists(flow.submitError) && (
              <HStack spacing={2}>
                <StyledButton buttonStyle="normal" onClick={goBack}>
                  {t('basic_output_back_to_confirmation')}
                </StyledButton>
              </HStack>
            )}
          </VStack>
        )
      default:
        return null
    }
  })()

  return (
    <VStack spacing={4}>
      <Typography variant="normal">
        {t('basic_output_step_indicator', { current: currentStep })}
      </Typography>
      {/* PageContainer's content area is overflow:hidden; each wizard step can
          exceed the viewport (step 4 in particular), so the step body owns its
          own vertical scroll. */}
      <div style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
        {stepContent}
      </div>
    </VStack>
  )
}
